import type { LoginCredentials } from "@itaas/core/auth/auth.types";
import type { User } from "@itaas/core/auth/models/user.model";
import { DEMO_CAPTURE_PROFILES } from "./demo-capture.config";
import {
  buildCurrentPageFilename,
  buildPageSlugFromLocation,
  resolveCaptureSelectorForPath,
  resolveProfileIdFromUser,
} from "./demo-capture-current";
import {
  buildCaptureFilename,
  resolveTourDefinition,
  type DemoCaptureMode,
  type DemoCaptureTourStep,
} from "./demo-capture.tours";
import {
  captureElementToBlob,
  resolveCaptureElement,
  saveCaptureBlob,
  setCaptureRunning,
  sleep,
  waitForPageReady,
} from "./demo-capture.utils";

export interface CaptureTourProgress {
  phase: "idle" | "login" | "navigate" | "wait" | "capture" | "done" | "error";
  mode: DemoCaptureMode;
  profileId?: string;
  profileLabel?: string;
  pageId?: string;
  pageLabel?: string;
  magazineSection?: string;
  current: number;
  total: number;
  saved: string[];
  errors: string[];
}

export interface CaptureTourCallbacks {
  onProgress: (progress: CaptureTourProgress) => void;
  navigate: (path: string) => void;
  login: (credentials: LoginCredentials) => Promise<void>;
  pickDirectory: () => Promise<FileSystemDirectoryHandle | null>;
}

export interface RunDemoCaptureTourOptions extends CaptureTourCallbacks {
  mode: DemoCaptureMode;
}

let lastLoggedEmail: string | null = null;

async function ensureLoggedIn(
  step: DemoCaptureTourStep,
  callbacks: CaptureTourCallbacks,
  progress: CaptureTourProgress,
): Promise<void> {
  if (lastLoggedEmail === step.email) return;

  progress.phase = "login";
  progress.profileId = step.profileId;
  progress.profileLabel = step.profileLabel;
  callbacks.onProgress({ ...progress });

  await callbacks.login({ email: step.email, password: step.password });
  lastLoggedEmail = step.email;
  await sleep(1500);
}

async function captureStep(
  step: DemoCaptureTourStep,
  mode: DemoCaptureMode,
  order: number,
  directory: FileSystemDirectoryHandle | null,
  callbacks: CaptureTourCallbacks,
  progress: CaptureTourProgress,
): Promise<void> {
  progress.profileId = step.profileId;
  progress.profileLabel = step.profileLabel;
  progress.pageId = step.page.id;
  progress.pageLabel = step.page.label;
  progress.magazineSection = step.magazineSection;

  progress.phase = "navigate";
  callbacks.onProgress({ ...progress });
  callbacks.navigate(step.page.path);

  progress.phase = "wait";
  callbacks.onProgress({ ...progress });
  await waitForPageReady(step.page.waitMs ?? 2800);

  progress.phase = "capture";
  callbacks.onProgress({ ...progress });
  await sleep(400);

  const filename = buildCaptureFilename(mode, step.profileId, step.page.id, order);
  try {
    const element = resolveCaptureElement(step.page.captureSelector);
    const blob = await captureElementToBlob(element);
    await saveCaptureBlob(blob, filename, directory);
    progress.saved.push(filename);
    progress.current += 1;
    callbacks.onProgress({ ...progress });
    await sleep(600);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    progress.errors.push(`${filename}: ${message}`);
    progress.current += 1;
    callbacks.onProgress({ ...progress });
  }
}

export async function runDemoCaptureTour(
  options: RunDemoCaptureTourOptions,
): Promise<CaptureTourProgress> {
  const { mode, ...callbacks } = options;
  const tour = resolveTourDefinition(mode, DEMO_CAPTURE_PROFILES);
  lastLoggedEmail = null;

  const progress: CaptureTourProgress = {
    phase: "idle",
    mode,
    current: 0,
    total: tour.steps.length,
    saved: [],
    errors: [],
  };

  setCaptureRunning(true);
  callbacks.onProgress({ ...progress });

  let directory: FileSystemDirectoryHandle | null = null;
  try {
    directory = await callbacks.pickDirectory();

    for (let i = 0; i < tour.steps.length; i += 1) {
      const step = tour.steps[i];
      await ensureLoggedIn(step, callbacks, progress);
      await captureStep(step, mode, i + 1, directory, callbacks, progress);
    }

    progress.phase = "done";
    callbacks.onProgress({ ...progress });
    return progress;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    progress.phase = "error";
    progress.errors.push(message);
    callbacks.onProgress({ ...progress });
    return progress;
  } finally {
    setCaptureRunning(false);
  }
}

export interface RunCaptureCurrentPageOptions {
  pathname: string;
  search: string;
  user: User | null;
  onProgress: (progress: CaptureTourProgress) => void;
  pickDirectory: () => Promise<FileSystemDirectoryHandle | null>;
}

export async function runCaptureCurrentPage(
  options: RunCaptureCurrentPageOptions,
): Promise<CaptureTourProgress> {
  const profileId = resolveProfileIdFromUser(options.user);
  const pageSlug = buildPageSlugFromLocation(options.pathname, options.search);
  const pageLabel = `${options.pathname}${options.search}`;

  const progress: CaptureTourProgress = {
    phase: "idle",
    mode: "current",
    current: 0,
    total: 1,
    profileId,
    pageId: pageSlug,
    pageLabel,
    saved: [],
    errors: [],
  };

  setCaptureRunning(true);
  options.onProgress({ ...progress });

  let directory: FileSystemDirectoryHandle | null = null;
  try {
    directory = await options.pickDirectory();

    progress.phase = "wait";
    options.onProgress({ ...progress });
    await waitForPageReady(2800);

    progress.phase = "capture";
    options.onProgress({ ...progress });
    await sleep(400);

    const filename = buildCurrentPageFilename(profileId, pageSlug);
    const selector = resolveCaptureSelectorForPath(options.pathname);
    try {
      const element = resolveCaptureElement(selector);
      const blob = await captureElementToBlob(element);
      await saveCaptureBlob(blob, filename, directory);
      progress.saved.push(filename);
      progress.current = 1;
      options.onProgress({ ...progress });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      progress.errors.push(`${filename}: ${message}`);
      progress.current = 1;
      options.onProgress({ ...progress });
    }

    progress.phase = "done";
    options.onProgress({ ...progress });
    return progress;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    progress.phase = "error";
    progress.errors.push(message);
    options.onProgress({ ...progress });
    return progress;
  } finally {
    setCaptureRunning(false);
  }
}

export type { DemoCaptureMode };
